interface FHIRBundle {
  resourceType: string;
  entry: Array<{
    fullUrl?: string;
    resource: any;
  }>;
}

interface AuthToken {
  access_token: string;
  id_token?: string;
  patient?: string;
}

export const downloadFHIRData = async (
  resourceTypes: string[], 
  serverURL: string, 
  auth: AuthToken
): Promise<FHIRBundle> => {
  const bundle: FHIRBundle & { inprogress?: Record<string, boolean> } = {
    resourceType: "Bundle",
    inprogress: {},
    entry: [],
  };

  const downloadAndMergeBundle = async (path: string) => {
    let url = serverURL + path;
    if (!serverURL.endsWith("/")) {
      url = serverURL + "/" + path;
    }
    if (url.indexOf("athena") > -1) { 
      url = url.replace("/?", "?"); 
    }
    
    if (bundle.inprogress![url]) {
      return;
    }
    
    const existing = bundle.entry.find(e => e.fullUrl === url);
    if (existing) {
      return;
    }
    
    bundle.inprogress![url] = true;
    
    try {
      const resp = await fetch(url, {
        headers: {
          Authorization: "Bearer " + auth.access_token,
          Accept: "application/fhir+json",
        },
      });
      const data = await resp.json();
      
      if (data && data.resourceType === 'Bundle' && data.entry) {
        bundle.entry = bundle.entry.concat(data.entry);
      } else if (data && data.resourceType) {
        bundle.entry.push({
          fullUrl: url,
          resource: data
        });
      }
    } catch (error) {
      console.error('Error downloading FHIR data:', error);
    }
    
    delete bundle.inprogress![url];
    
    // Filter out operation outcomes and entered-in-error records
    bundle.entry = bundle.entry.filter(e => e?.resource?.resourceType !== "OperationOutcome");
    bundle.entry = bundle.entry.filter(e => {
      if (e.resource?.verificationStatus) {
        if (e.resource.verificationStatus.coding?.length > 0) {
          return e.resource.verificationStatus.coding[0].code !== "entered-in-error";
        }
        return e.resource.verificationStatus.text !== "Entered in Error";
      } else if (e.resource?.status) {
        return e.resource.status !== "entered-in-error";
      }
      return true;
    });
  };

  const downloadDependentData = async () => {
    const promises: Promise<void>[] = [];
    
    bundle.entry.forEach((e) => {
      if (e.resource.resourceType === "CareTeam" && e.resource.participant) {
        e.resource.participant.forEach((p: any) => {
          if (p.member?.reference) {
            promises.push(downloadAndMergeBundle(p.member.reference));
          }
        });
      } else if (e.resource.resourceType === "MedicationRequest") {
        if (e.resource.medicationReference?.reference) {
          promises.push(downloadAndMergeBundle(e.resource.medicationReference.reference));
        }
        if (e.resource.requester?.reference) {
          promises.push(downloadAndMergeBundle(e.resource.requester.reference));
        }
      }
    });
    
    await Promise.all(promises);
  };

  // Extract patient ID from id_token if not provided
  if (auth.id_token && !auth.patient) {
    try {
      const tokenParts = auth.id_token.split('.');
      if (tokenParts.length > 2) {
        const payload = JSON.parse(atob(tokenParts[1]));
        if (payload.profile) {
          auth.patient = payload.profile.substring(serverURL.length + 8);
        } else if (payload.fhirUser) {
          auth.patient = payload.fhirUser.substring(serverURL.length + 8);
        }
      }
    } catch (error) {
      console.error('Error parsing id_token:', error);
    }
  }

  // Download requested resource types
  const downloadPromises: Promise<void>[] = [];
  
  if (resourceTypes.includes("Patient") && auth.patient) {
    downloadPromises.push(downloadAndMergeBundle(`Patient/?_id=${auth.patient}`));
  }
  if (resourceTypes.includes("Allergy")) {
    downloadPromises.push(downloadAndMergeBundle(`AllergyIntolerance/?patient=${auth.patient}`));
  }
  if (resourceTypes.includes("Procedure")) {
    downloadPromises.push(downloadAndMergeBundle(`Procedure/?patient=${auth.patient}`));
  }
  if (resourceTypes.includes("Condition")) {
    downloadPromises.push(downloadAndMergeBundle(`Condition/?patient=${auth.patient}`));
  }
  if (resourceTypes.includes("Immunization")) {
    downloadPromises.push(downloadAndMergeBundle(`Immunization/?patient=${auth.patient}`));
  }
  if (resourceTypes.includes("MedicationRequest")) {
    let extra = "";
    if (serverURL.indexOf("athena") > -1) {
      extra = "&intent=order";
    }
    downloadPromises.push(downloadAndMergeBundle(`MedicationRequest/?patient=${auth.patient}&status=active${extra}`));
  }
  if (resourceTypes.includes("MedicationDispense")) {
    downloadPromises.push(downloadAndMergeBundle(`MedicationDispense/?patient=${auth.patient}`));
  }
  if (resourceTypes.includes("Observation")) {
    // Epic requires specific parameters for Observation queries
    // Download vital signs observations
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&category=vital-signs`));
    
    // Download specific lab observations needed for scoring
    // Blood pressure components
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=8480-6`)); // Systolic BP
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=8462-4`)); // Diastolic BP
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=55284-4`)); // BP Panel
    
    // Cholesterol
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=2089-1`)); // LDL
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=13457-7`)); // LDL direct
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=18262-6`)); // LDL calculated
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=2085-9`)); // HDL
    
    // HbA1c
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=4548-4`)); // HbA1c
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=17856-6`)); // HbA1c
    
    // BMI
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=39156-5`)); // BMI
    
    // Mental health assessments
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=44255-8`)); // PHQ9
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=44249-1`)); // PHQ9
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=44250-9`)); // PHQ9
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=69744-3`)); // GAD7
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=76515-4`)); // Social engagement
    downloadPromises.push(downloadAndMergeBundle(`Observation/?patient=${auth.patient}&code=68514-3`)); // Social engagement
  }
  if (resourceTypes.includes("QuestionnaireResponse")) {
    // Note: QuestionnaireResponse not available due to insufficient OAuth scope
    // Mental health data will be extracted from Observation resources only
  }
  if (resourceTypes.includes("Encounter")) {
    downloadPromises.push(downloadAndMergeBundle(`Encounter/?patient=${auth.patient}`));
  }
  if (resourceTypes.includes("Appointment")) {
    downloadPromises.push(downloadAndMergeBundle(`Appointment/?patient=${auth.patient}`));
  }
  if (resourceTypes.includes("CareTeam")) {
    downloadPromises.push(downloadAndMergeBundle(`CareTeam/?patient=${auth.patient}`));
  }

  await Promise.all(downloadPromises);
  await downloadDependentData();

  // Clean up the inprogress tracking
  delete bundle.inprogress;

  return bundle as FHIRBundle;
};

// IndexedDB storage for FHIR data
class FHIRDataStore {
  private dbName = 'HealthBeaconFHIR';
  private version = 1;
  private storeName = 'fhir_bundles';

  async initDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'patientId' });
        }
      };
    });
  }

  async storeFHIRData(patientId: string, bundle: any): Promise<void> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      await new Promise((resolve, reject) => {
        const request = store.put({ patientId, bundle, timestamp: Date.now() });
        request.onsuccess = () => resolve(undefined);
        request.onerror = () => reject(request.error);
      });
      console.log(`FHIR data stored in IndexedDB for patient: ${patientId}`);
    } catch (error) {
      console.error('IndexedDB storage failed:', error);
      throw error;
    }
  }

  async getFHIRData(patientId: string): Promise<any | null> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      return new Promise((resolve, reject) => {
        const request = store.get(patientId);
        request.onsuccess = () => {
          const result = request.result;
          resolve(result ? result.bundle : null);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('IndexedDB retrieval failed:', error);
      throw error;
    }
  }

  async listAllPatients(): Promise<Array<{patientId: string, bundle: any, timestamp: number}>> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => {
          const results = request.result as Array<{patientId: string, bundle: any, timestamp: number}>;
          resolve(results);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('IndexedDB listAllPatients failed:', error);
      return [];
    }
  }

  async listPatients(): Promise<string[]> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve, reject) => {
        const request = store.getAllKeys();
        request.onsuccess = () => {
          const keys = request.result as string[];
          resolve(keys);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.warn('IndexedDB list failed, falling back to localStorage:', error);
      // Fallback to localStorage
      const patients: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('fhir_data_')) {
          patients.push(key.replace('fhir_data_', ''));
        }
      }
      return patients;
    }
  }

  async deleteFHIRData(patientId: string): Promise<void> {
    try {
      const db = await this.initDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      await new Promise((resolve, reject) => {
        const request = store.delete(patientId);
        request.onsuccess = () => resolve(undefined);
        request.onerror = () => reject(request.error);
      });
      
      console.log(`FHIR data deleted from IndexedDB for patient: ${patientId}`);
    } catch (error) {
      console.warn('IndexedDB deletion failed, falling back to localStorage:', error);
      // Fallback to localStorage
      localStorage.removeItem(`fhir_data_${patientId}`);
    }
  }
}

// Global instance
export const fhirDataStore = new FHIRDataStore();

// Helper function to optimize FHIR bundle by removing duplicates and keeping most recent records
export const optimizeFHIRBundle = (bundle: FHIRBundle): FHIRBundle => {
  if (!bundle || !bundle.entry) return bundle;

  console.log(`Original bundle size: ${bundle.entry.length} entries`);

  // Group entries by resource type and identifier
  const groupedEntries = new Map<string, any[]>();

  bundle.entry.forEach(entry => {
    const resource = entry.resource;
    if (!resource || !resource.resourceType) return;

    // Create a key based on resource type and identifier
    let key = resource.resourceType;
    
    // For observations, include the LOINC code to group similar measurements
    if (resource.resourceType === 'Observation' && resource.code?.coding?.[0]?.code) {
      key += `_${resource.code.coding[0].code}`;
    }
    // For conditions, include the condition code
    else if (resource.resourceType === 'Condition' && resource.code?.coding?.[0]?.code) {
      key += `_${resource.code.coding[0].code}`;
    }
    // For medications, include the medication code
    else if (resource.resourceType === 'MedicationRequest' && resource.medicationCodeableConcept?.coding?.[0]?.code) {
      key += `_${resource.medicationCodeableConcept.coding[0].code}`;
    }
    // For procedures, include the procedure code
    else if (resource.resourceType === 'Procedure' && resource.code?.coding?.[0]?.code) {
      key += `_${resource.code.coding[0].code}`;
    }
    // For encounters, include the encounter type
    else if (resource.resourceType === 'Encounter' && resource.type?.[0]?.coding?.[0]?.code) {
      key += `_${resource.type[0].coding[0].code}`;
    }

    if (!groupedEntries.has(key)) {
      groupedEntries.set(key, []);
    }
    groupedEntries.get(key)!.push(entry);
  });

  // Keep only the most recent entry for each group
  const optimizedEntries: any[] = [];

  groupedEntries.forEach((entries, key) => {
    if (entries.length === 1) {
      // Only one entry, keep it
      optimizedEntries.push(entries[0]);
    } else {
      // Multiple entries, find the most recent one
      let mostRecent = entries[0];
      let mostRecentDate = getResourceDate(entries[0].resource);

      entries.forEach(entry => {
        const date = getResourceDate(entry.resource);
        if (date > mostRecentDate) {
          mostRecent = entry;
          mostRecentDate = date;
        }
      });

      optimizedEntries.push(mostRecent);
    }
  });

  console.log(`Optimized bundle size: ${optimizedEntries.length} entries (${Math.round((1 - optimizedEntries.length / bundle.entry.length) * 100)}% reduction)`);

  return {
    ...bundle,
    entry: optimizedEntries
  };
};

// Helper function to get the most relevant date from a resource
const getResourceDate = (resource: any): Date => {
  // Try different date fields in order of preference
  const dateFields = [
    'effectiveDateTime',
    'issued',
    'performedDateTime',
    'authoredOn',
    'date',
    'onsetDateTime',
    'recordedDate',
    'created'
  ];

  for (const field of dateFields) {
    if (resource[field]) {
      const date = new Date(resource[field]);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }

  // If no valid date found, return a very old date
  return new Date('1900-01-01');
};

export const importDataFrom = async (serverURL: string, auth: AuthToken): Promise<void> => {
  console.log('Importing FHIR data from:', serverURL);
  console.log('Auth token:', JSON.stringify(auth, null, 2));

  try {
    const bundle = await downloadFHIRData([
      'Patient',
      'CareTeam',
      'Allergy',
      'Condition',
      'Procedure',
      'Immunization',
      'MedicationRequest',
      'MedicationDispense',
      'Observation',
      'Encounter'
    ], serverURL, auth);

    console.log('Downloaded FHIR bundle:', bundle);

    // Optimize the bundle by removing duplicates and keeping most recent records
    const optimizedBundle = optimizeFHIRBundle(bundle);

    // Extract patient information
    const patients = optimizedBundle.entry
      .filter(e => e.resource.resourceType === 'Patient')
      .map(e => e.resource);

    if (patients.length > 0) {
      const patient = patients[0];
      const patientId = patient.id;
      // Store the optimized bundle in IndexedDB
      await fhirDataStore.storeFHIRData(patientId, optimizedBundle);
      console.log(`Optimized bundle size: ${JSON.stringify(optimizedBundle).length} bytes`);
      console.log(`Bundle stored in IndexedDB for patient: ${patientId}`);
      // Also store patient-specific info for easy access in localStorage (optional, not bundle)
      const patientInfo = {
        id: patient.id,
        name: patient.name?.[0] ? {
          given: patient.name[0].given?.join(' ') || '',
          family: patient.name[0].family || ''
        } : null,
        birthDate: patient.birthDate || '',
        telecom: patient.telecom || []
      };
      localStorage.setItem(`patient_info_${patientId}`, JSON.stringify(patientInfo));
      console.log(`FHIR data stored for patient: ${patientId}`);
      // Download the bundle as a JSON file for offline access
      const patientName = patientInfo.name ? 
        `${patientInfo.name.given} ${patientInfo.name.family}`.trim() : 
        patientId;
      const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
      const filename = `fhir_bundle_${patientName.replace(/\s+/g, '_')}_${timestamp}.json`;
      // Create and download the file
      const blob = new Blob([JSON.stringify(optimizedBundle, null, 2)], { 
        type: 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log(`FHIR bundle downloaded as: ${filename}`);
    }
  } catch (error) {
    console.error('Error importing FHIR data:', error);
    throw error;
  }
};

export const loadFHIRData = async (patientId: string): Promise<FHIRBundle | null> => {
  try {
    // Only use IndexedDB
    const bundle = await fhirDataStore.getFHIRData(patientId);
    if (bundle) {
      console.log(`Loaded FHIR bundle from IndexedDB for patient: ${patientId}`);
      console.log(`Bundle size: ${JSON.stringify(bundle).length} bytes`);
      return bundle;
    }
    console.log(`No FHIR data found for patient: ${patientId}`);
    return null;
  } catch (error) {
    console.error('Error loading FHIR data:', error);
    return null;
  }
};
